import { Box, Tooltip } from "@mui/material";
import { alpha, darken, type Theme } from "@mui/material/styles";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { t } from "@utils/i18n";
import type { ReactNode } from "react";

interface HiddenInDatasetBadgeProps {
  size?: "small" | "medium";
  className?: string;
  /** Renders this instead of the default "Hidden in dataset" text — e.g. the
   *  item's own label — with the explanation moved into a hover tooltip
   *  instead of shown inline. */
  children?: ReactNode;
  /** Forces the amber background/padding chip look even with custom
   *  children — used where the original chip treatment should stay (e.g.
   *  the base condition editor's saved-selection chips). Defaults to false:
   *  children render as plain icon + warning-colored text sized to match
   *  the surrounding copy, for use inline within running text. */
  withBackground?: boolean;
  /** Overrides the hover tooltip's generic "Hidden in dataset" text with a
   *  more specific explanation (only used when children are provided —
   *  without children, the text itself already says "Hidden in dataset"). */
  tooltipTitle?: string;
}

/**
 * Marks a saved source (question/variable/value/bar) whose dataset visibility
 * is now hidden. Styled as an amber warning chip to match the drawer's
 * "This chart uses content that is now hidden…" banner.
 */
export default function HiddenInDatasetBadge({
  size = "small",
  className = "",
  children,
  withBackground = false,
  tooltipTitle,
}: HiddenInDatasetBadgeProps) {
  const iconSize = size === "small" ? 16 : 18;
  const showChip = !children || withBackground;

  const content = (
    <Box
      component="span"
      className={className}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        // vertical-align only applies to inline-level boxes, not flex
        // children — it has to live here, on the flex container itself, to
        // align this span with the surrounding text's baseline. Putting it
        // on the icon (a flex item) is silently ignored by the spec.
        verticalAlign: "middle",
        gap: 0.5,
        whiteSpace: "nowrap",
        // Match MUI's standard warning Alert text color (the drawer banner),
        // which is darken(warning.light, 0.6) — not the more-orange warning.dark.
        color: (theme) => darken(theme.palette.warning.light, 0.6),
        ...(showChip
          ? {
              px: 0.75,
              py: 0.25,
              // Matches MUI Chip's pill shape (its default border-radius is
              // 16px regardless of size) so hidden/non-hidden chips look
              // consistent sitting side by side.
              borderRadius: "16px",
              fontSize: size === "small" ? 13 : 14,
              fontWeight: 500,
              lineHeight: 1.4,
              bgcolor: (theme: Theme) => alpha(theme.palette.warning.main, 0.14),
            }
          : // Plain-text variant: inherit the surrounding copy's size/weight/
            // line-height exactly, so it reads as styled text rather than a
            // chip of a different size sitting on its own line.
            {
              fontSize: "inherit",
              fontWeight: "inherit",
              lineHeight: "inherit",
            }),
      }}
    >
      <ReportProblemOutlinedIcon
        sx={{
          fontSize: showChip ? iconSize : "1em",
          color: "warning.main",
        }}
      />
      {children ?? t("common.hiddenInDataset")}
    </Box>
  );

  return children ? (
    <Tooltip title={tooltipTitle ?? t("common.hiddenInDataset")}>
      {content}
    </Tooltip>
  ) : (
    content
  );
}
